import { css } from '@emotion/react';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import {
  Accordion, AccordionHeaderSize,
} from '~/_shared/baseComponents/accordion';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { BoundaryItemContainer } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryItem.container';
import { BoundaryTerritoryContainer } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryTerritory.container';
import type { SortedBTGs } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryTools.types';
import { useBoundaryItems } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryItems';
import { useBoundaryTerritoryGroupDisplayName } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryTerritoryGroupDisplayName';
import { useBTGFillData } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBTGFillData';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { useGetBoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { useActiveBTGIdPerBoundaryGroup } from '~/store/mapSettings/toolsState/boundary/activeBTG.selectors';

const headingStyle = css({
  display: 'flex',
  flexDirection: 'column',
  fontSize: 12,
});

const panelStyle = css({
  padding: 0,
});

const entityWrapperStyles = css({
  maxWidth: '100%',
  minWidth: 0,
});

const mainEntityStyles = (theme: Theme) => css({
  color: theme.textColors.primary,
  fontSize: 12,
  display: 'flex',
  alignItems: 'center',
});

const subEntityStyles = (theme: Theme) => css({
  color: theme.textColors.primary,
  fontSize: 16,
  lineHeight: '18px', // to support underline letters
  paddingTop: 1,
});

const boundaryNameStyles = (theme: Theme) => css({
  color: theme.textColors.secondary,
  fontSize: 12,
  paddingTop: 1,
});

const ellipsisStyle = css({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

const entityChevronStyle = css({
  paddingLeft: 4,
});

export const BoundaryTerritoryGroupKey = ({
  boundaryGroupId,
  boundaryTerritoryGroups,
  isExpanded,
  onToggle,
}: {
  boundaryGroupId: BoundaryGroupId;
  boundaryTerritoryGroups: SortedBTGs;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const { getBoundaryTerritoryGroupById } = useGetBoundaryTerritoryGroup();
  const activeBTGId = useActiveBTGIdPerBoundaryGroup(boundaryGroupId) ?? boundaryTerritoryGroups[1]?.boundaryTerritoryGroupId ?? boundaryTerritoryGroups[0]?.boundaryTerritoryGroupId;
  const activeBoundaryTerritoryGroup = getBoundaryTerritoryGroupById(activeBTGId) ?? boundaryTerritoryGroups[1] ?? boundaryTerritoryGroups[0];
  const { getBoundaryTerritoryGroupName } = useBoundaryTerritoryGroupDisplayName();

  const {
    activeBTGOption,
  } = useBTGFillData({
    activeBoundaryTerritoryGroup,
    boundaryTerritoryGroups,
  });

  const {
    boundaryTerritoryList,
    displayBoundaryTerritories,
    areGroupBoundariesEmpty,
    sortedBoundaryList,
    boundaryPermissions,
  } = useBoundaryItems({ boundaryTerritoryGroup: activeBoundaryTerritoryGroup });

  const { shortenedSubEntity, mainEntity } = activeBTGOption?.entities || {};

  return (
    <Accordion.Item
      onToggle={onToggle}
      open={isExpanded}
    >
      <Accordion.ItemHeading
        size={AccordionHeaderSize.Large}
      >
        <div css={headingStyle}>
          <div css={entityWrapperStyles}>
            <div css={mainEntityStyles}>
              <div
                css={ellipsisStyle}
              >
                {mainEntity}
              </div>
              {shortenedSubEntity && (
                <FontAwesomeIcon
                  css={entityChevronStyle}
                  icon={faChevronRight}
                  size="xs"
                />
              )}
            </div>
            {shortenedSubEntity && (
              <div
                css={[subEntityStyles, ellipsisStyle]}
              >
                {shortenedSubEntity}
              </div>
            )}
            <div
              css={[boundaryNameStyles, ellipsisStyle]}
            >
              {getBoundaryTerritoryGroupName(activeBoundaryTerritoryGroup)}
            </div>
          </div>
        </div>
      </Accordion.ItemHeading>
      <Accordion.Panel css={panelStyle}>
        {displayBoundaryTerritories && boundaryTerritoryList.map(boundaryTerritory => {
          return (
            <BoundaryTerritoryContainer
              key={boundaryTerritory.boundaryTerritoryId}
              boundaryTerritory={boundaryTerritory}
              boundaryTerritoryGroup={activeBoundaryTerritoryGroup}
              legendMode
            />
          );
        })}
        {!areGroupBoundariesEmpty && !displayBoundaryTerritories &&
          sortedBoundaryList.map(boundary => {
            return (
              <BoundaryItemContainer
                boundary={boundary}
                boundaryPermissions={boundaryPermissions}
                boundaryTerritoryGroup={activeBoundaryTerritoryGroup}
                key={boundary.id}
                legendMode
              />
            );
          })
        }
      </Accordion.Panel>
    </Accordion.Item>
  );
};
